<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { ADS_WORKSPACE_ROUTE_NAME } from '../routes';
import { useAdsI18n } from '../useAdsI18n';
import { useRastroEnabled } from '../useRastro';
import { usePolicy } from 'dashboard/composables/usePolicy';

const route = useRoute();
const { t } = useAdsI18n();
const { checkPermissions } = usePolicy();
const isRastroEnabled = useRastroEnabled();
const canShowSidebar = computed(
  () => isRastroEnabled.value && checkPermissions(['administrator'])
);
const mountTarget = ref(null);
const isCollapsed = ref(false);
let resizeObserver;
let sidebarObserver;
let refreshFrame;

const removeMountTarget = () => {
  mountTarget.value?.parentElement?.removeChild(mountTarget.value);
  mountTarget.value = null;
};

const isActive = computed(() => route.name === ADS_WORKSPACE_ROUTE_NAME);
const routeTarget = computed(() => ({
  name: ADS_WORKSPACE_ROUTE_NAME,
  params: { accountId: route.params.accountId },
}));

const refreshMountTarget = async () => {
  await nextTick();
  if (!canShowSidebar.value) {
    removeMountTarget();
    return;
  }
  const sidebar = document.querySelector('aside');
  if (!sidebar) {
    removeMountTarget();
    return;
  }

  const reportsLink = Array.from(sidebar.querySelectorAll('a')).find(link =>
    /\/reports(?:\?.*)?$/.test(link.getAttribute('href') || '')
  );
  const reportsItem =
    reportsLink?.closest('li') ||
    sidebar.querySelector('.i-lucide-chart-spline')?.closest('li');

  if (!reportsItem?.parentElement) {
    removeMountTarget();
    return;
  }

  const existingTarget = reportsItem.parentElement.querySelector(
    '[data-ads-sidebar-target]'
  );
  if (existingTarget) {
    mountTarget.value = existingTarget;
  } else {
    const target = document.createElement('li');
    target.dataset.adsSidebarTarget = '';
    target.className = 'grid min-w-0 cursor-pointer select-none';
    reportsItem.parentElement.insertBefore(target, reportsItem);
    mountTarget.value = target;
  }

  resizeObserver?.disconnect();
  resizeObserver = new ResizeObserver(entries => {
    isCollapsed.value = entries[0]?.contentRect.width < 80;
  });
  resizeObserver.observe(sidebar);
};

const scheduleRefresh = () => {
  if (refreshFrame) return;
  refreshFrame = requestAnimationFrame(() => {
    refreshFrame = null;
    refreshMountTarget();
  });
};

watch(canShowSidebar, scheduleRefresh);
watch(() => route.fullPath, scheduleRefresh);
onMounted(async () => {
  await refreshMountTarget();
  const sidebar = document.querySelector('aside');
  if (!sidebar) return;

  sidebarObserver = new MutationObserver(scheduleRefresh);
  sidebarObserver.observe(sidebar, { childList: true, subtree: true });
});
onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  sidebarObserver?.disconnect();
  if (refreshFrame) cancelAnimationFrame(refreshFrame);
  removeMountTarget();
});
</script>

<template>
  <Teleport v-if="canShowSidebar" :to="mountTarget || 'body'">
    <RouterLink
      v-if="mountTarget"
      :to="routeTarget"
      :title="t('ads.sidebar.label')"
      :aria-label="t('ads.sidebar.label')"
      :aria-current="isActive ? 'page' : undefined"
      class="flex h-8 min-w-0 items-center gap-2 rounded-lg px-1.5 py-1"
      :class="[
        isCollapsed ? 'size-10 justify-center p-0' : '',
        isActive
          ? 'bg-n-alpha-2 font-medium text-n-slate-12'
          : 'text-n-slate-11 hover:bg-n-alpha-2',
      ]"
    >
      <span class="i-lucide-route size-4 shrink-0" />
      <span v-if="!isCollapsed" class="truncate text-body-main">
        {{ t('ads.sidebar.label') }}
      </span>
    </RouterLink>
  </Teleport>
</template>
